import { Bot, Eye, EyeOff } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

export default function Login() {
  const [email, setEmail] = useState('')
  const [pw, setPw] = useState('')
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPw, setShowPw] = useState(false)
  const [remember, setRemember] = useState(false)
  const [time, setTime] = useState(new Date())
  const { login, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [sp] = useSearchParams()

  useEffect(() => {
    if (localStorage.getItem('login_remember') === 'true') {
      setEmail(localStorage.getItem('login_email') || '')
      setRemember(true)
    }
  }, [])

  // When isAuthenticated becomes true after login(), navigate to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate(sp.get('returnUrl') || '/dashboard', { replace: true })
    }
  }, [isAuthenticated]) // eslint-disable-line

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 30000)
    return () => clearInterval(t)
  }, [])

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    setErr('')
    setLoading(true)
    try {
      await login(email, pw)
      if (remember) {
        localStorage.setItem('login_email', email)
        localStorage.setItem('login_remember', 'true')
      } else {
        localStorage.removeItem('login_email')
        localStorage.removeItem('login_remember')
      }
      // navigate is handled by useEffect above when isAuthenticated becomes true
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }, [email, pw, remember, login])

  return (
    <div className="au">
      <style>{CSS}</style>
      <div className="au-bg" />
      <div className="au-grid">
        <div className="au-side">
          <div className="au-time">{time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
          <div className="au-date">{time.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
        </div>
        <div className="au-main">
          <div className="au-avatar"><Bot size={32} color="#818cf8" /></div>
          <div className="au-title">RunAI Cloud</div>
          <form onSubmit={e => { e.preventDefault(); void submit(e) }} className="au-form">
            {err && <div className="au-err">{err}</div>}
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" required autoFocus className="au-inp" />
            <div className="au-pw">
              <input type={showPw ? 'text' : 'password'} value={pw} onChange={e => setPw(e.target.value)} placeholder="Password" required className="au-inp au-inp--pw" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="au-pw-btn">{showPw ? <EyeOff size={14} /> : <Eye size={14} />}</button>
            </div>
            <div className="au-rem">
              <input type="checkbox" id="rem" checked={remember} onChange={e => setRemember(e.target.checked)} />
              <label htmlFor="rem">Remember me</label>
            </div>
            <button type="submit" disabled={loading} className="au-btn">{loading ? '...' : 'Sign In'}</button>
          </form>
          <Link to="/register" className="au-link">Create Account</Link>
          <Link to="/forgot-password" className="au-link">Forgot Password</Link>
        </div>
      </div>
    </div>
  )
}

const CSS = `
.au{width:100vw;height:100vh;height:100dvh;display:flex;align-items:center;justify-content:center;background:#000;position:relative;overflow:hidden}
.au-bg{position:absolute;inset:0;background-image:url('/wallpaper-night.jpg');background-size:cover;background-position:center;filter:brightness(0.55) saturate(1.2)}
.au-bg::after{content:'';position:absolute;inset:0;background:linear-gradient(160deg,rgba(12,10,29,0.6) 0%,rgba(26,14,58,0.5) 25%,rgba(45,22,84,0.4) 45%,rgba(26,39,68,0.5) 65%,rgba(12,25,41,0.6) 85%,rgba(6,13,24,0.7) 100%);backdrop-filter:blur(2px)}
.au-grid{z-index:2;display:flex;align-items:center;justify-content:center;gap:clamp(2rem,6vmin,5rem);width:100%;height:100%;padding:4vmin;box-sizing:border-box}
.au-side{text-align:center;flex-shrink:0}
.au-time{font-size:clamp(2.5rem,7vmin,6rem);font-weight:700;color:#fff;letter-spacing:-0.04em;line-height:1;text-shadow:0 2px 16px rgba(0,0,0,0.3)}
.au-date{font-size:clamp(0.8rem,1.8vmin,1.2rem);color:rgba(255,255,255,0.65);margin-top:0.3em}
.au-main{display:flex;flex-direction:column;align-items:center;width:min(380px,90vw);flex-shrink:0}
.au-avatar{width:clamp(3rem,6vmin,4.5rem);height:clamp(3rem,6vmin,4.5rem);border-radius:50%;background:linear-gradient(145deg,rgba(255,255,255,0.15),rgba(255,255,255,0.05));backdrop-filter:blur(20px);border:2px solid rgba(255,255,255,0.15);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 16px rgba(0,0,0,0.4)}
.au-title{font-size:clamp(1rem,2.2vmin,1.3rem);font-weight:600;color:#fff;margin:0.5em 0 1em}
.au-form{width:100%}
.au-inp{width:100%;padding:14px 16px;margin-bottom:10px;background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.12);border-radius:10px;color:#fff;font-size:16px;outline:none;box-sizing:border-box;font-family:inherit;backdrop-filter:blur(10px);transition:border-color 0.2s}
.au-inp::placeholder{color:rgba(255,255,255,0.35)}
.au-inp:focus{border-color:rgba(129,140,248,0.6);background:rgba(255,255,255,0.1)}
.au-inp--pw{padding-right:2.8rem}
.au-pw{position:relative;width:100%}
.au-pw-btn{position:absolute;right:0.7rem;top:50%;transform:translateY(-50%);background:none;border:none;color:rgba(255,255,255,0.35);cursor:pointer;padding:4px}
.au-rem{display:flex;align-items:center;gap:0.4em;margin:0.3em 0 0.8em}
.au-rem input{width:1em;height:1em;accent-color:#818cf8}
.au-rem label{font-size:13px;color:rgba(255,255,255,0.5);cursor:pointer}
.au-err{padding:10px 14px;margin-bottom:10px;border-radius:8px;background:rgba(239,68,68,0.12);border:1px solid rgba(239,68,68,0.25);color:#fca5a5;font-size:13px;text-align:center}
.au-btn{width:100%;padding:14px;border:none;border-radius:10px;background:rgba(255,255,255,0.12);color:#fff;font-size:16px;font-weight:600;cursor:pointer;font-family:inherit;backdrop-filter:blur(10px);transition:all 0.2s}
.au-btn:hover{background:rgba(255,255,255,0.18)}
.au-btn:disabled{opacity:0.5;cursor:wait}
.au-link{margin-top:0.8em;color:rgba(255,255,255,0.35);font-size:13px;text-decoration:none;transition:color 0.2s}
.au-link:hover{color:rgba(255,255,255,0.7)}
@media(orientation:portrait){.au-grid{flex-direction:column;gap:2vmin}}
@media(max-height:400px){.au-side{display:none}.au-avatar{display:none}.au-title{display:none}}
`
